/**
 * Utility for persisting File objects in IndexedDB to survive browser refreshes.
 */

const DB_NAME = 'CampaignWizardDB';
const DB_VERSION = 1;
const STORE_NAME = 'csv_files';

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
};

export const filePersistence = {
  /**
   * Save a File object to IndexedDB
   */
  saveFile: async (key: string, file: File): Promise<void> => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(file, key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to save file to IndexedDB:', error);
    }
  },

  /**
   * Load a File object from IndexedDB
   */
  loadFile: async (key: string): Promise<File | null> => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => resolve(request.result as File || null);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to load file from IndexedDB:', error);
      return null;
    }
  },

  /**
   * Delete a File object from IndexedDB
   */
  clearFile: async (key: string): Promise<void> => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to clear file from IndexedDB:', error);
    }
  }
};
